/**
 * Geo auto-route for multi-region programs (migration 0163).
 *
 * Generalizes the BKOS Founding Cohort AU auto-route into one reusable hook: a
 * visitor who lands on a program's *default* page (no region in the URL) and
 * whose IP country maps to a non-default regional cohort is redirected to that
 * region's page, so they get the right coach, dates, local time, and currency.
 *
 * - Only fires on the default page (no `regionParam`) — the region pages never
 *   redirect, so there's no loop.
 * - Dormant until a program has >1 regional cohort with `countries` populated
 *   (so single-region programs and the NA-only launch are unaffected).
 * - Detection is IP-based (GET /api/public/geo → ISO-2), best-effort: any error
 *   leaves the visitor on the default page.
 */
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const API = import.meta.env.VITE_API_BASE_URL as string;

interface RegionLink {
  code: string;
  label: string;
  isDefault: boolean;
  countries: string[];
}

export function useRegionRedirect(
  slug: string,
  regionParam: string | undefined,
  regions: RegionLink[] | undefined,
): void {
  const navigate = useNavigate();

  useEffect(() => {
    if (regionParam) return; // already on a region page — never redirect
    if (!regions || regions.length < 2) return; // single-region → nothing to do
    // Only meaningful if at least one non-default region declares its countries.
    const targets = regions.filter((r) => !r.isDefault && r.countries?.length);
    if (!targets.length) return;

    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/api/public/geo`);
        if (!res.ok) return;
        const d = (await res.json()) as { country?: string };
        const country = String(d.country ?? '').toUpperCase();
        if (cancelled || !country) return;
        const match = targets.find((r) => r.countries.map((c) => c.toUpperCase()).includes(country));
        if (match) navigate(`/programs/${slug}/${match.code.toLowerCase()}`, { replace: true });
      } catch {
        /* best-effort — stay on the default page */
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, regionParam, regions?.length]);
}
